import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { I18nextProvider, useTranslation } from 'react-i18next';

import { LS_INTERFACE_LANGUAGE } from '@/shared/constants/localStorage';
import {
  AppLocale,
  getActiveInterfaceLocale,
  getBrowserLanguage,
  getInitialInterfaceLocale,
  getStoredInterfaceLocale,
  isAppLocale,
  resolveInterfaceLocale,
  setActiveInterfaceLocale,
  storeInterfaceLocale,
} from '@/shared/i18n/locale';
import { interfaceI18n, translate, Translate } from '@/shared/i18n/translations';

type InterfaceLanguageContextValue = {
  hasExplicitLocale: boolean;
  locale: AppLocale;
  t: Translate;
  setLocale: (locale: AppLocale) => void;
};

type InterfaceLanguageProviderProps = {
  children: ReactNode;
  initialLocale?: AppLocale;
  onLocaleChange?: (locale: AppLocale, browserLanguage: string) => void;
};

const InterfaceLanguageContext = createContext<InterfaceLanguageContextValue>({
  hasExplicitLocale: false,
  locale: getActiveInterfaceLocale(),
  t: (key, variables) => translate(getActiveInterfaceLocale(), key, variables),
  setLocale: () => undefined,
});

type InterfaceLanguageBridgeProps = { children: ReactNode } & Pick<
  InterfaceLanguageContextValue,
  'hasExplicitLocale' | 'locale' | 'setLocale'
>;

const InterfaceLanguageBridge = ({ children, hasExplicitLocale, locale, setLocale }: InterfaceLanguageBridgeProps) => {
  const { t: translateKey } = useTranslation();
  const t: Translate = useCallback(
    (key, variables) => translateKey(key, { ...variables, lng: locale }),
    [locale, translateKey],
  );
  const value = useMemo(() => ({ hasExplicitLocale, locale, t, setLocale }), [hasExplicitLocale, locale, setLocale, t]);

  return <InterfaceLanguageContext.Provider value={value}>{children}</InterfaceLanguageContext.Provider>;
};

export const InterfaceLanguageProvider = ({
  children,
  initialLocale = getInitialInterfaceLocale(),
  onLocaleChange,
}: InterfaceLanguageProviderProps) => {
  const [locale, setCurrentLocale] = useState<AppLocale>(() => {
    setActiveInterfaceLocale(initialLocale);
    void interfaceI18n.changeLanguage(initialLocale);
    return initialLocale;
  });
  const [hasExplicitLocale, setHasExplicitLocale] = useState(() => Boolean(getStoredInterfaceLocale()));

  useEffect(() => {
    document.documentElement.lang = locale;
    onLocaleChange?.(locale, getBrowserLanguage());
  }, [locale, onLocaleChange]);

  useEffect(() => {
    const handleStorage = ({ key, newValue }: StorageEvent) => {
      if (key !== LS_INTERFACE_LANGUAGE) {
        return;
      }

      let nextLocale: AppLocale | undefined;

      if (isAppLocale(newValue)) {
        nextLocale = newValue;
      } else if (newValue === null) {
        nextLocale = resolveInterfaceLocale(getBrowserLanguage());
      }

      if (!nextLocale) {
        return;
      }

      setActiveInterfaceLocale(nextLocale);
      void interfaceI18n.changeLanguage(nextLocale);
      setHasExplicitLocale(isAppLocale(newValue));
      setCurrentLocale(nextLocale);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setLocale = useCallback((nextLocale: AppLocale) => {
    setActiveInterfaceLocale(nextLocale);
    void interfaceI18n.changeLanguage(nextLocale);
    storeInterfaceLocale(nextLocale);
    setHasExplicitLocale(true);
    setCurrentLocale(nextLocale);
  }, []);

  return (
    <I18nextProvider i18n={interfaceI18n}>
      <InterfaceLanguageBridge hasExplicitLocale={hasExplicitLocale} locale={locale} setLocale={setLocale}>
        {children}
      </InterfaceLanguageBridge>
    </I18nextProvider>
  );
};

export const useInterfaceLanguage = () => {
  return useContext(InterfaceLanguageContext);
};
